// API service for communicating with the FastAPI backend

import axios from 'axios';
import { 
  User, 
  Question, 
  TestSubmission, 
  DetailedTestResult, 
  LoginRequest, 
  RegisterRequest, 
  AuthResponse,
  UserListResponse,
  SystemStats,
  TestResult,
  IELTSMockTest,
  IELTSTestSubmission,
  IELTSTestResult,
  IELTSBandScoreInfo,
  Vocabulary,
  VocabularyListResponse,
  UserVocabularyProgress,
  FlashcardSession,
  FlashcardSessionCreate,
  VocabularyStats,
  CEFRLevel,
  Topic,
  VocabularyFilters
} from '../types';

// Create axios instance with base configuration
const api = axios.create({
  baseURL: process.env.NODE_ENV === 'production' 
    ? '/api'  // Use relative URL in production (nginx will proxy)
    : 'http://localhost:8000/api',  // Use localhost in development
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('access_token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: async (data: LoginRequest): Promise<AuthResponse> => {
    const response = await api.post('/auth/login', data);
    return response.data;
  },

  register: async (data: RegisterRequest): Promise<User> => {
    const response = await api.post('/auth/register', data);
    return response.data;
  },

  getCurrentUser: async (): Promise<User> => {
    const response = await api.get('/auth/me');
    return response.data;
  },
};

// Questions API
export const questionsAPI = {
  getAllQuestions: async (): Promise<Question[]> => {
    const response = await api.get('/questions/');
    return response.data;
  },

  getPlacementTestQuestions: async (): Promise<Question[]> => {
    const response = await api.get('/questions/placement-test/questions');
    return response.data;
  },

  getQuestion: async (id: number): Promise<Question> => {
    const response = await api.get(`/questions/${id}`);
    return response.data;
  },
};

// Tests API
export const testsAPI = {
  submitTest: async (data: TestSubmission): Promise<DetailedTestResult> => {
    const response = await api.post('/tests/submit', data);
    return response.data;
  },

  getTestResult: async (id: number): Promise<DetailedTestResult> => {
    const response = await api.get(`/tests/results/${id}`);
    return response.data;
  },
};

// Users API
export const usersAPI = {
  getProfile: async (): Promise<User> => {
    const response = await api.get('/users/profile');
    return response.data;
  },

  updateProfile: async (data: Partial<User>): Promise<User> => {
    const response = await api.put('/users/profile', data);
    return response.data;
  },

  activateAccount: async (): Promise<{ message: string }> => {
    const response = await api.post('/users/activate');
    return response.data;
  },

  getTestHistory: async (): Promise<TestResult[]> => {
    const response = await api.get('/users/test-history');
    return response.data;
  },
};

// Admin API
export const adminAPI = {
  getUsers: async (page: number = 1, size: number = 10): Promise<UserListResponse> => {
    const response = await api.get(`/admin/users?page=${page}&size=${size}`);
    return response.data;
  },

  getUser: async (id: number): Promise<User> => {
    const response = await api.get(`/admin/users/${id}`);
    return response.data;
  },

  updateUser: async (id: number, data: Partial<User>): Promise<User> => {
    const response = await api.put(`/admin/users/${id}`, data);
    return response.data;
  },

  deleteUser: async (id: number): Promise<{ message: string }> => {
    const response = await api.delete(`/admin/users/${id}`);
    return response.data;
  },

  getSystemStats: async (): Promise<SystemStats> => {
    const response = await api.get('/admin/stats');
    return response.data;
  },

  getAllTestResults: async (): Promise<TestResult[]> => {
    const response = await api.get('/admin/test-results');
    return response.data;
  },

  getTestResultDetails: async (id: number): Promise<DetailedTestResult> => {
    const response = await api.get(`/admin/test-results/${id}`);
    return response.data;
  },
};

// IELTS Mock Exam API
export const ieltsAPI = {
  getTests: async (testType?: string): Promise<IELTSMockTest[]> => {
    const params = testType ? { test_type: testType } : {};
    const response = await api.get('/ielts/tests', { params });
    return response.data;
  },

  getTest: async (testId: number): Promise<IELTSMockTest> => {
    const response = await api.get(`/ielts/tests/${testId}`);
    return response.data;
  },

  startTest: async (testId: number): Promise<{ test_result_id: number; message: string }> => {
    const response = await api.post(`/ielts/tests/${testId}/start`);
    return response.data;
  },

  submitTest: async (submission: IELTSTestSubmission): Promise<IELTSTestResult> => {
    const response = await api.post('/ielts/tests/submit', submission);
    return response.data;
  },

  getTestResult: async (resultId: number): Promise<IELTSTestResult> => {
    const response = await api.get(`/ielts/results/${resultId}`);
    return response.data;
  },

  getBandScores: async (): Promise<IELTSBandScoreInfo[]> => {
    const response = await api.get('/ielts/band-scores');
    return response.data;
  },
};

// Vocabulary Builder API
export const vocabularyAPI = {
  getWords: async (filters: VocabularyFilters = {}): Promise<VocabularyListResponse> => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value.toString());
      }
    });
    const response = await api.get(`/vocabulary/words?${params.toString()}`);
    return response.data;
  },

  getWord: async (id: number): Promise<Vocabulary> => {
    const response = await api.get(`/vocabulary/words/${id}`);
    return response.data;
  },

  getRandomWord: async (cefrLevel?: string, topic?: string): Promise<Vocabulary> => {
    const params = new URLSearchParams();
    if (cefrLevel) params.append('cefr_level', cefrLevel);
    if (topic) params.append('topic', topic);
    const response = await api.get(`/vocabulary/words/random?${params.toString()}`);
    return response.data;
  },

  getLevels: async (): Promise<CEFRLevel[]> => {
    const response = await api.get('/vocabulary/levels');
    return response.data;
  },

  getTopics: async (): Promise<Topic[]> => {
    const response = await api.get('/vocabulary/topics');
    return response.data;
  },

  getProgress: async (): Promise<UserVocabularyProgress[]> => {
    const response = await api.get('/vocabulary/progress');
    return response.data;
  },

  updateProgress: async (wordId: number, masteryLevel: number, isCorrect: boolean): Promise<{ message: string }> => {
    const response = await api.post(`/vocabulary/progress/${wordId}`, null, {
      params: { mastery_level: masteryLevel, is_correct: isCorrect }
    });
    return response.data;
  },

  getFlashcards: async (cefrLevel?: string, topic?: string, count: number = 10): Promise<Vocabulary[]> => {
    const params = new URLSearchParams();
    if (cefrLevel) params.append('cefr_level', cefrLevel);
    if (topic) params.append('topic', topic);
    params.append('count', count.toString());
    const response = await api.get(`/vocabulary/flashcards?${params.toString()}`);
    return response.data;
  },

  createFlashcardSession: async (sessionData: FlashcardSessionCreate): Promise<FlashcardSession> => {
    const response = await api.post('/vocabulary/flashcards/session', sessionData);
    return response.data;
  },

  getFlashcardSessions: async (limit: number = 20): Promise<FlashcardSession[]> => {
    const response = await api.get(`/vocabulary/flashcards/sessions?limit=${limit}`);
    return response.data;
  },

  getStats: async (): Promise<VocabularyStats> => {
    const response = await api.get('/vocabulary/stats');
    return response.data;
  },
};

export default api;
